import api from '../services/authService';
import Cookies from 'js-cookie';
import { getUserId } from "../utils/fetchLocalStorage";
// sample API structure
export const fetchMatter = async (page, status, case_type, sort_order, sort_by, page_size, search = "", assigned_to_me = false) => {
    const params = new URLSearchParams({
      page: page.toString(),
      status: status || "",
      case_type: case_type || "",
      sort_order: sort_order || "",
      sort_by: sort_by || "",
      page_size: page_size.toString(),
      search: search
    });
    if (assigned_to_me) {
      params.append("assigned_to_me", "true");
    }
    const response = await api.get(`/matters/?${params.toString()}`);
    return response;
};

export const fetchMatterDetails = async (id) => {
  const response = await api.get(`/matters/${id}/`);
  return response;
};
export const fetchMatterDetailFolders = async (id) => {
  const response = await api.get(`/folders/${id}/`);
  return response;
}
export const fetchMatterDetailsList = async ({ id, page, pageSize, sort_direction, sort_by, folder }) => {
  let url = `/matters/${id}/folder-hierarchy/?page=${page}&&page_size=${pageSize}`;
  if (folder) {
    url = `${url}&&folder_id=${folder}`
  }
  if (sort_direction) {
    url = `${url}&&sort_direction=${sort_direction}`
  }
  if (sort_by) {
    url = `${url}&&sort_by=${sort_by}`
  }
  const response = await api.get(url)
  return response;
};
export const fetchCategoryLists = async () => {
  const response = await api.get(`/documents/categories/`);
  return response;
}

export const categoryUpdate = async ({ id, category }) => {
  const response = await api.post(`/documents/${id}/category/update/`, { category });
  return response
}

// Placement-only move: reassigns the document's folder without touching
// category fields (unlike /recategorize/, which moves under AI Claim
// File/<category>). folderId of null moves the document to the matter root.
export const moveDocumentApi = async ({ id, folderId, onConflict }) => {
  const body = { folder_id: folderId ?? null };
  // "rename" tells the backend to keep both (auto-suffix) instead of
  // 409ing on a destination name collision. Omitted => default "error".
  if (onConflict) body.on_conflict = onConflict;
  const response = await api.post(`/documents/${id}/move/`, body);
  return response;
};

// One request moves a whole multi-select. Answers 200 with per-document
// outcomes in `moved` / `conflicts` / `failed`: a name collision is an
// outcome, not a failure, so the clean files land either way.
export const moveDocumentsBatchApi = async ({
  documentIds,
  folderId,
  onConflict,
}) => {
  const body = {
    document_ids: documentIds,
    folder_id: folderId ?? null,
  };
  if (onConflict) body.on_conflict = onConflict;
  const response = await api.post(`/documents/move-batch/`, body);
  return response;
};

// Paste half of cross-matter copy/paste: copies each document into the
// target matter/folder as a new first-class document (fresh bytes +
// identity). Per-file failures come back in response.data.results.
export const copyDocumentsApi = async ({
  documentIds,
  targetMatterId,
  targetFolderId,
}) => {
  const response = await api.post(`/documents/copy/`, {
    document_ids: documentIds,
    target_matter_id: targetMatterId,
    target_folder_id: targetFolderId ?? null,
  });
  return response;
};

export const fetchOpenDocsList = async (id) => {
  const userId = getUserId();
  const response = await api.get(`/documents/tabs/${userId}/`, {
    params: { matter_id: id }
  });
  return response;
};

export const postOpenDoc = async (matterId, docId) => {
  const userId = getUserId();
  const response = await api.post(`/documents/tabs/${userId}/`, {
    matter_id: String(matterId),
    document_id: String(docId),
  }
  );
  return response;
};

export const removeOpenDoc = async (matterId, docId) => {
  const userId = getUserId();
  const response = await api.delete(`/documents/tabs/${userId}/`, {
    data: {
      matter_id: String(matterId),
      document_id: String(docId),
    }
  });
  return response;
};

export const deleteDocumentApi = async (docId) => {
  const response = await api.delete(`/documents/${docId}/delete/`, {
    data: {
    }
  });
  return response;
};

export const bulkDeleteMatterDocuments = async (matterId, documentIds) => {
  const response = await api.post(`/matters/${matterId}/documents/bulk-delete/`, {
    document_ids: documentIds,
  });
  return response;
};

export const reviewActionApi = async (
  shareId,
  reviewStatus,
  comment,
  nextReviewerId = null
) => {
  const response = await api.post(`/document-share/actions/${shareId}/`, {
    status: reviewStatus,
    notes: comment,
    ...(nextReviewerId ? { next_reviewer_id: nextReviewerId } : {}),
  });
  return response;
};

/** `resolvedOn` (YYYY-MM-DD) names the day a complete/dismiss actually
 * happened — a brief filed Monday and closed out Wednesday should read
 * Monday. Omitted means the server stamps now. */
export const reviewQueueActionApi = async (
  shareId,
  action,
  notes = "",
  { resolvedOn } = {}
) => {
  return api.post(`/document-share/actions/${shareId}/${action}/`, {
    notes,
    ...(resolvedOn ? { resolved_on: resolvedOn } : {}),
  });
};

export const deleteFolderApi = async (folderId) => {
  const response = await api.delete(`/folders/${folderId}/delete/`, {
    data: {
    }
    });
  return response;
};

const buildFilterNotificationParams = ({
  timeFilter,
  customFilter,
  customDate,
  startDate,
  endDate,
  unread,
}) => {
  const params = new URLSearchParams();

  if (unread) params.append("unread", "true");
  if (timeFilter) params.append("time_filter", timeFilter);

  if (customFilter) params.append("custom_filter", customFilter);

  if (customFilter === "custom_date" && customDate) {
    params.append("custom_date", customDate);
  }

  if (customFilter === "date_range") {
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
  }

  return params;
};


export const fetchNotification = async (data) => {
  const { offset, limit } = data;

  const params = buildFilterNotificationParams(data);

  params.append("limit", limit || 8);
  params.append("offset", offset);

  const response = await api.get(`/notifications/?${params.toString()}`);
  return response;
};

export const getShareRecipientsApi = async (docId, reviewersOnly) => {
  const query = reviewersOnly ? '?reviewers_only=true' : '';
  const path =
    docId == null || docId === ""
      ? `/document-share/users/${query}`
      : `/document-share/users/${docId}/${query}`;
  const response = await api.get(path);
  return response;
};


export const shareDocumentApi = async (payload) => {
  const response = await api.post(
    `/document-share/create/`,
    { ...payload }
  );
  return response;
};

export const remindReviewApi = async (shareId) => {
  const response = await api.post(
    `/document-share/remind/${shareId}/`,
    {}
  );
  return response;
};

export const remindSignatureApi = async (signatureId) => {
  const response = await api.post(
    `/e-signature/remind/${signatureId}/`,
    {}
  );
  return response;
};
 export const updateNotification = async (notification_ids) =>{
  const response = await api.post(
    `/notifications/`,
     notification_ids
  );
  return response;
};

export const fetchReviewNotesApi = async (documentId) => {
  const response = await api.get(`/documents/${documentId}/review-notes/`);
  return response;
}

export const getShareContext = async (docId) => {
  const response = await api.get(
    `/documents/${docId}/sharing-context/`,
    {}
  );
  return response;
};
export const submitActionApi = async (docId, comment) => {
  const response = await api.post(`/documents/${docId}/submit/`, {
    submission_notes: comment,
  });
  return response;
};

export const postMatterHighlight = async (matterId, itemID, itemType, tabAction = null) => {
  const payload = {
    item_type: itemType,
    item_id: itemID
  };
  
  // Include tab_action for documents only
  if (tabAction && itemType === "document") {
    payload.tab_action = tabAction;
  }
  
  const response = await api.post(`/matters/${matterId}/highlight/`, payload);
  return response;
}

export const postLastPageView = async (docId,page_number) => {
  const response = await api.post (`/documents/${docId}/page-view/`,{
    page_number:page_number
  });
  return response;
}
 
export const fetchTemplateFieldsApi = async (templateId,matter_id) => {
  let url = `/formstack/templates/${templateId}/fields`;
  if(matter_id){
    url = `${url}/?matter_id=${matter_id}`
  }
  const response = await api.get(url);
  return response;
};

export const mergeTemplateFieldsApi = async (payload) => {
  const response = await api.post(
    `/formstack/templates/${payload.templateId}/merge/?${new URLSearchParams(payload.query)}`,
    payload.results
  );
  return response;
};

export const previewTemplateFieldsApi = async (payload) => {
  const response = await api.post(
    `/formstack/templates/${payload.templateId}/preview/?${new URLSearchParams(payload.query)}`,
    {"merge_data":payload.results},
    {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      responseType: 'blob'
    }
  );
  return response;
};

export const uploadTemplateApi = async (formData) => {
  const response = await api.post(
    `/formstack/templates/upload/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    }
  );
  return response;
};

export const deleteMatterHighlight = async (matterId) => {
  const response = await api.delete(`/matters/${matterId}/highlight/`, {
    
  });
  return response;
}

export const sendForESignApi = async (payload) => {
  const response = await api.post(
    `e-signature/create-request/`,
    payload
  );
  return response;
};

export const fetchReadyToSignFormsApi = async () => {
  const response = await api.get(`e-signature/ready-forms/`);
  return response;
};

export const fetchSignableDocumentsApi = async ({ matterId, search = "", folder = "" }) => {
  const response = await api.get(`e-signature/signable-documents/`, {
    params: { matter_id: matterId, ...(search ? { search } : {}), ...(folder ? { folder } : {}) },
  });
  return response;
};

export const fetchSignableFoldersApi = async (matterId) => {
  const response = await api.get(`e-signature/signable-documents/folders/`, {
    params: { matter_id: matterId },
  });
  return response;
};

export const fetchSuggestedESignRecipientsApi = async (documentId) => {
  const response = await api.get(`e-signature/suggested-recipients/${documentId}/`);
  return response;
};

export const initiateESignApi = async (payload) => {
  const response = await api.post(
    `e-signature/initiate/${payload.id}/`,
    payload
  );
  return response;
};

export const cancelSignatureRequestApi = async (requestId) => {
  const response = await api.post(`e-signature/cancel/${requestId}/`, {});
  return response;
};

export const fetchExternalShareDetail= async ({document_id}) =>{
  const response = await api.get(`/documents/${document_id}/external-share-info/`);
  return response;
}

export const generatePassword= async () =>{
  const response = await api.get(`/generate-password/`);
  return response;
}
export const externalShare = async (payload) => {
  const response = await api.post(
    `/external-share/create/`,
    payload
  );
  return response;
};
export const revokeShare = async ({document_id}) => {
  const response = await api.post(
    `/documents/${document_id}/revoke-all-external-shares/`
  );
  return response;
};

export const getDocumentDetail = async (id) =>{
  const response = await api.get(`/documents/${id}/`);
  return response;
}

export const fetchPreviewPDF = async (docId, key) => {
  const response = await api.get(`/formstack/templates/${docId}/preview/`, {
    query: {
      route_key: key,
      output: 'pdf'
    }
  });
  return response;
}

export const fetchBreadCrumb = async (item_id)=>{
  const response = await api.get(`/breadcrumb/${item_id}/`);
  return response;
}

export const fetchBookmarksAPI = async (docId) => {
  const response = await api.get(`/documents/bookmarks/`, {
    params: { doc_id: docId },
  });
  return response;
};

export const getBookmarksAPI = async (payload) => {
  const { doc_id, bookmarks } = payload || {};
  const response = await api.post(`/documents/bookmarks/`, { doc_id, bookmarks });
  return response;
};

export const fetchDocumentAnnotations = async (documentId) => {
  let docId = String(documentId || "").trim();
  if (docId.startsWith("D") && docId.length > 1) {
    docId = docId.substring(1);
  }
  const response = await api.get(`/documents/${docId}/annotations/`);
  return response.data;
};

export const saveDocumentAnnotations = async (documentId, payload) => {
  let docId = String(documentId || "").trim();
  if (docId.startsWith("D") && docId.length > 1) {
    docId = docId.substring(1);
  }
  const response = await api.post(`/documents/${docId}/annotations/`, payload);
  return response.data;
};

export const fetchVAFormData = async (id) => {
  const response = await api.get(`/documents/${id}/va-form-data/`);
  return response;
};

export const sendVAFormData = async (payload) => {
  const response = await api.post(
    `/documents/${payload?.document_id}/send-to-va/`,
    payload
  );
  return response;
};

// The look at the pages before the send (Matthew, 2026-10-30): which VA form
// this is, whether every required signature box is signed and dated, whether
// the veteran named on the form is the one VA will be told about, and whether
// the pages are readable. Same body as send-to-va; returns stops / warnings /
// passes, each stop with a picture of the outlined box.
export const reviewVAPages = async (payload) => {
  const response = await api.post(
    `/documents/${payload?.document_id}/va-page-review/`,
    payload
  );
  return response;
};

// For documents over the VA's 100 MB limit: the backend compresses a copy
// in the background and submits that instead. Returns 202 + upload_id.
export const compressAndSendVAFormData = async (payload) => {
  const response = await api.post(
    `/documents/${payload?.document_id}/compress-and-send-to-va/`,
    payload
  );
  return response;
};

export const updateTabordering = async( { matter_id, document_id, new_position }) => {
  const userId = getUserId();
  const response = await api.put(
    `/documents/tabs/${userId}/`,
    { matter_id, document_id, new_position }
  );
  return response;
};

export const chatBotApi = async (payload) => {
  const response = await api.post(
    `/agent/chatbot/`,
    payload
  );
  return response;
};

// A thread is keyed by its scope, so the collection id has to travel with
// both the lookup and the archive-and-restart. Collection scope wins over
// document scope when both are somehow set — the backend resolves it the
// same way, and sending both would otherwise key two different threads
// depending on which side decided.
export const fetchCurrentChatThread = async ({
  matterId,
  documentId,
  collectionId,
}) => {
  const params = new URLSearchParams({ matter_id: String(matterId) });
  if (collectionId) {
    params.set('collection_id', String(collectionId));
  } else if (documentId) {
    params.set('document_id', String(documentId));
  }
  return api.get(`/agent/chat/threads/current/?${params.toString()}`);
};

export const startNewChatThread = async ({
  matterId,
  documentId,
  collectionId,
}) =>
  api.post(`/agent/chat/threads/current/`, {
    matter_id: String(matterId),
    ...(collectionId
      ? { collection_id: String(collectionId) }
      : documentId
        ? { document_id: String(documentId) }
        : {}),
  });

/**
 * Authenticated POST-SSE client. Axios buffers response bodies, so typed
 * questions use native fetch + ReadableStream while welcome/options retain
 * the existing JSON thunk.
 */
export const streamChatBot = async (payload, { signal, onEvent }) => {
  const base = process.env.REACT_APP_API_URL || '/api/';
  const url = `${base.replace(/\/?$/, '/')}agent/chatbot/stream/`;
  const csrfToken = Cookies.get('csrftoken');
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Chat stream failed (${response.status}): ${body}`);
  }
  if (!response.body) throw new Error('Chat stream has no response body');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const emitFrame = (frame) => {
    if (!frame.trim()) return;
    let event = 'message';
    let data = {};
    for (const line of frame.split(/\r?\n/)) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      if (line.startsWith('data:')) {
        const raw = line.slice(5).trim();
        data = raw ? JSON.parse(raw) : {};
      }
    }
    onEvent?.(event, data);
  };
  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const frames = buffer.split(/\r?\n\r?\n/);
    buffer = frames.pop() || '';
    for (const frame of frames) emitFrame(frame);
    if (done) break;
  }
  // Some proxies close immediately after the final data line without a
  // trailing blank line. Do not silently drop the terminal done/citations
  // frame left in the buffer.
  emitFrame(buffer);
};

export const fetchLetterheadList = async () => {
  const response = await api.get(`/settings/letterhead-templates/`);
  return response;
};

export const fetchPresignedUrl = async (documentId, options = {}) => {
  const response = await api.get(`documents/presigned-url/${documentId}/`, {
    params: options.download ? { download: 1 } : undefined,
  });
  return response.data;
};

export const processDocumentPipeline = async (documentId, { force = false } = {}) => {
  const params = force ? '?force=true' : '';
  const response = await api.post(`/pipeline/process/${documentId}/${params}`);
  return response;
};

// Retry the document(s) that failed processing on a matter.
//
// Backed by `POST /api/matters/<id>/documents/retry-errors/` (see
// `dms_backend` PRs #655 / #656). Pass `documentIds` to retry a specific
// subset (per-doc "Retry" button); omit it to retry every failed document
// on the matter (header-level "Retry all" button). The backend caps the
// number of jobs it will resubmit in a single call to 200 and surfaces
// that via `truncated: true` + `total_candidate_count` in the response,
// so the caller can tell the user they need to click again to drain the
// rest. Orphan documents — rows whose `pipeline_status` is `failed` but
// that never had a real `ExternalPipelineJob` to resubmit — come back in
// `orphan_documents` so the UI can flag them as "needs a fresh upload"
// instead of silently skipping them.
export const retryMatterErrors = async (matterId, { documentIds } = {}) => {
  const payload = {};
  if (Array.isArray(documentIds)) payload.document_ids = documentIds;
  const response = await api.post(
    `/matters/${matterId}/documents/retry-errors/`,
    payload
  );
  return response;
};

// Categorization management
export const fetchCategorizationStats = async () => {
  const response = await api.get("/pipeline/categorization/stats/");
  return response.data;
};

export const fetchGoldStandards = async (category) => {
  // Pass grouped=1 to keep the legacy grouped-by-category response shape
  // (`{ gold_standards: { Category: [...] } }`) that the AI Categorization
  // Settings page reads today. BE PR #512 (feat/categorization-mgmt-api)
  // switches the default response to a paginated flat list — without
  // grouped=1 the existing page would break the moment that PR deploys.
  // The new /src/service/categorization.js client reads the paginated
  // default; this helper is only used by the legacy Settings reader.
  const usp = new URLSearchParams({ grouped: "1" });
  if (category) usp.set("category", category);
  const response = await api.get(
    `/pipeline/categorization/gold-standards/?${usp.toString()}`
  );
  return response.data;
};

export const addGoldStandard = async (documentId, category) => {
  const response = await api.post("/pipeline/categorization/gold-standards/", {
    document_id: documentId,
    category,
  });
  return response.data;
};

export const removeGoldStandard = async (id) => {
  await api.delete(`/pipeline/categorization/gold-standards/${id}/`);
};

// The Notification Letter exemplar library: a deliberately separate
// shelf from the category gold standards. Each row is one real VA
// decision-notification cover letter plus the ground truth of what was
// bound behind it in that scan (rating decision / codesheet / nothing).
// It teaches ONLY the packet-recognition question; category filing
// rules are untouched.
export const fetchNotificationLetterExemplars = async () => {
  const response = await api.get(
    "/pipeline/categorization/notification-letter-exemplars/"
  );
  return response.data;
};

export const removeNotificationLetterExemplar = async (id) => {
  await api.delete(
    `/pipeline/categorization/notification-letter-exemplars/${id}/`
  );
};

export const addNotificationLetterExemplar = async ({
  documentId,
  enclosedSections,
  notes,
}) => {
  const response = await api.post(
    "/pipeline/categorization/notification-letter-exemplars/",
    {
      document_id: documentId,
      enclosed_sections: enclosedSections,
      ...(notes ? { notes } : {}),
    }
  );
  return response.data;
};

export const patchNotificationLetterExemplar = async (id, data) => {
  const response = await api.patch(
    `/pipeline/categorization/notification-letter-exemplars/${id}/`,
    data
  );
  return response.data;
};

export const fetchNegativeExamples = async (category) => {
  // grouped=1 — same back-compat reason as fetchGoldStandards above.
  const usp = new URLSearchParams({ grouped: "1" });
  if (category) usp.set("category", category);
  const response = await api.get(
    `/pipeline/categorization/negative-examples/?${usp.toString()}`
  );
  return response.data;
};

export const addNegativeExample = async (documentId, negativeForCategory, reason) => {
  const response = await api.post("/pipeline/categorization/negative-examples/", {
    document_id: documentId,
    negative_for_category: negativeForCategory,
    reason: reason || "",
  });
  return response.data;
};

export const removeNegativeExample = async (id) => {
  await api.delete(`/pipeline/categorization/negative-examples/${id}/`);
};

export const correctDocumentCategory = async (documentId, data) => {
  const response = await api.post(`/pipeline/categorization/correct/${documentId}/`, data);
  return response.data;
};

export const fetchCategorizationPredictions = async ({ category, correctedOnly, limit } = {}) => {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  if (correctedOnly) params.set("corrected_only", "true");
  if (limit) params.set("limit", limit);
  const qs = params.toString();
  const response = await api.get(`/pipeline/categorization/predictions/${qs ? `?${qs}` : ""}`);
  return response.data;
};

// ── Reference Library ───────────────────────────────────────────────

export const fetchReferenceSources = async () => {
  const response = await api.get("/pipeline/references/");
  return response.data;
};

export const uploadReferenceDocument = async (file, title) => {
  const formData = new FormData();
  formData.append("file", file);
  if (title) formData.append("title", title);
  const response = await api.post(
    "/pipeline/references/upload",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/json",
      },
    }
  );
  return response.data;
};

export const startReferenceCrawl = async (url, title) => {
  const response = await api.post("/pipeline/references/crawl", { url, title });
  return response.data;
};

export const searchReferenceLibrary = async (query, topK = 5) => {
  const params = new URLSearchParams({ q: query, top_k: topK });
  const response = await api.get(`/pipeline/references/search?${params}`);
  return response.data;
};

export const deleteReferenceSource = async (id) => {
  await api.delete(`/pipeline/references/${id}/`);
};